import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPinIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { fetchStateList } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

const regionLabels: Record<string, string> = {
  Northeast: 'Northeast',
  Midwest: 'Midwest',
  South: 'South',
  West: 'West',
};

const regionOrder = ['Northeast', 'Midwest', 'South', 'West'];

const regionDescriptions: Record<string, string> = {
  Northeast:
    'Nine states including New York, Pennsylvania, Massachusetts, and New Jersey. Each has its own Medicaid waivers, AAAs, and senior benefit programs.',
  Midwest:
    'Twelve states including Ohio, Illinois, Michigan, and Minnesota. Look up your state for HCBS waivers, tax benefits, and aging services.',
  South:
    'Seventeen states including Florida, Texas, Georgia, and North Carolina. Florida and Texas rank among the top three states by senior population nationwide.',
  West:
    'Thirteen states including California, Washington, Arizona, and Colorado. California has the largest senior population in the country.',
};

/**
 * Hub for state-level pSEO pages. Lists all published states grouped by US Census region.
 * Individual state pages live at /states/[slug]. See .claude/plans/silly-dreaming-knuth.md.
 */
const StatesIndex = () => {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['pseo-states-list'],
    queryFn: fetchStateList,
    staleTime: 15 * 60 * 1000,
  });

  const grouped = (entries || []).reduce<Record<string, typeof entries>>((acc, entry) => {
    const region = entry.region || 'Other';
    if (!acc[region]) acc[region] = [];
    acc[region]!.push(entry);
    return acc;
  }, {});

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'State Resources', url: `${BASE_URL}/states` },
  ];

  return (
    <>
      <SEO
        title="State-by-State Guide to Aging in Place Resources"
        description="Medicaid waivers, Area Agencies on Aging, tax benefits, and fall prevention programs for seniors in all 50 states. Sourced from official state and federal agencies."
        keywords="aging in place by state, state Medicaid waivers, HCBS waiver, Area Agency on Aging, senior resources by state"
        url={`${BASE_URL}/states`}
        canonical={`${BASE_URL}/states`}
        breadcrumbs={breadcrumbs}
      />

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <header className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full dark:bg-blue-950/50 mb-6">
              <MapPinIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Aging in Place by State
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Medicaid waivers, Area Agencies on Aging, senior tax benefits, and fall prevention
              programs. Every fact cited to a state or federal agency.
            </p>
          </header>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : entries && entries.length > 0 ? (
            regionOrder
              .filter((region) => grouped[region]?.length)
              .map((region) => (
                <section key={region} className="mb-12">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {regionLabels[region] || region}
                  </h2>
                  {regionDescriptions[region] && (
                    <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                      {regionDescriptions[region]}
                    </p>
                  )}
                  <div className="grid gap-3 md:grid-cols-2">
                    {grouped[region]!.map((entry) => (
                      <Link
                        key={entry.slug}
                        to={`/states/${entry.slug}`}
                        className="group flex items-center justify-between gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/40 transition-colors"
                      >
                        <div>
                          <div className="font-bold text-foreground group-hover:text-primary transition-colors">
                            {entry.state_name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Aging-in-place resources
                          </div>
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                      </Link>
                    ))}
                  </div>
                </section>
              ))
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>State resource pages are coming soon. Check back shortly.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StatesIndex;
